import { computed, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { DemoAccountService } from 'src/shared/services/demo-account.service';
import { PmDemoLandingService } from 'src/shared/services/pm-demo-landing.service';
import { PmDemoTourService } from 'src/shared/services/pm-demo-tour.service';

export interface DemoTourStep {
  id: string;
  route: string;
  // Short display name used for the "Next: X" button (matches the nav labels).
  name: string;
  icon: string;
  title: string;
  // One line max — the dock must never read as a wall of text.
  body: string;
  // 2–3 word feature chips rendered as pills, not sentences.
  chips: string[];
  // Selectors for the page's nav entry (desktop left nav, mobile bottom nav);
  // the dock puts a glow ring on it — "this is the page you're looking at".
  navTargets: string[];
  // When the page's nav entry lives inside a collapsed nav section, the section
  // header to click open (once per stop) so the real entry can be highlighted.
  navSectionToggle?: string;
}

interface PersistedTourState {
  order: string[];
  index: number;
}

/**
 * Guided tour shown once per PM demo session, starting when the "Welcome to TIDY"
 * card is dismissed. Each stop is one of the five pm-demo-landing variant pages,
 * presented in a glass guide dock at the bottom of the viewport (see
 * DemoTourComponent) that sells what the current page does; "Next" walks the
 * remaining pages so every visitor sees all five regardless of which variant they
 * landed on (the cycle is rotated to start at their landing page).
 *
 * The page stays fully interactive during the tour, so the tour FOLLOWS the
 * router: navigating to another tour page jumps the dock to that stop, and
 * navigating anywhere off the tour path dismisses it gracefully.
 *
 * State lives in localStorage so a mid-tour reload resumes on the same stop, and a
 * dismissal sticks. Both keys are wiped by the demo login's storage clear, so a
 * fresh demo visit on the same device gets the tour again.
 *
 * The tour is the `guided-tour` arm of the desktop-only pm-demo-tour experiment
 * (PmDemoTourService): control — and every mobile visitor, who is never assigned a
 * variant — sees no dock at all.
 */
@Injectable({ providedIn: 'root' })
export class DemoTourService {

  private static readonly STATE_KEY = 'demoTourState';
  private static readonly DONE_KEY = 'demoTourDone';

  // The full cycle, ordered to match the nav top-to-bottom (Chat, Overview,
  // Schedule, Revenue → Optimizer, Properties) so the tour reads as walking DOWN
  // the menu — the rotation preserves that direction from any landing page.
  // Copy sells TIDY as full property management: each page is the client's
  // window into work TIDY is doing for them.
  private static readonly STEPS: DemoTourStep[] = [
    {
      id: 'chat',
      route: 'chat',
      name: 'Chat',
      icon: 'forum',
      title: 'Talk to Us Anytime, 24/7',
      body: 'Just ask — "book a cleaning Friday" — and we\'ll handle it.',
      chips: ['Book by chat', 'Instant answers', 'Any property task'],
      navTargets: ['[data-testid="nav-chat"]', '[data-testid="bottom-nav-/chat"]'],
    },
    {
      // Step ids are what PostHog records for the tour, so they keep the old page names.
      id: 'strategy',
      route: 'overview',
      name: 'Overview',
      icon: 'trending_up',
      title: 'Performance, Profit, and Projections',
      body: 'Revenue, expenses, and net income for every property — tracked for you.',
      chips: ['Live P&L', 'Projected vs. actual', 'AI profit tips'],
      navTargets: ['[data-testid="nav-strategy"]', '[data-testid="bottom-nav-/overview"]'],
    },
    {
      id: 'schedule',
      route: 'schedule',
      name: 'Schedule',
      icon: 'event_available',
      title: 'Turnovers that Run Themselves',
      body: 'Your bookings sync automatically, and cleanings are scheduled between every stay.',
      chips: ['Auto-booked turnovers', 'Synced reservations', 'Pros assigned'],
      navTargets: ['[data-testid="nav-schedule"]', '[data-testid="bottom-nav-/schedule"]'],
    },
    {
      id: 'optimizer',
      route: 'optimizer',
      name: 'Optimizer',
      icon: 'tune',
      title: 'Your Whole Strategy, Optimized',
      body: 'Pricing, discounts, and booking strategy — tuned daily to earn more.',
      chips: ['Dynamic pricing', 'Smart discounts', 'Channel strategy'],
      // Desktop: the Optimizer entry inside the Revenue section (auto-expanded via
      // navSectionToggle), falling back to the section header. Mobile: More.
      navTargets: [
        '[data-testid="nav-marketing-optimizer"]',
        '[data-testid="nav-section-marketing"]',
        '[data-testid="bottom-nav-/more"]',
      ],
      navSectionToggle: '[data-testid="nav-section-marketing"]',
    },
    {
      id: 'overview',
      route: 'properties',
      name: 'Properties',
      icon: 'home_work',
      title: 'A Live Pulse on Every Property',
      body: 'Your whole portfolio, live — see what\'s happening at every property.',
      chips: ['Live map', 'Activity feed', 'One-tap actions'],
      navTargets: ['[data-testid="nav-properties"]', '[data-testid="bottom-nav-/properties"]'],
    },
  ];

  readonly active = signal(false);
  readonly leaving = signal(false);
  readonly steps = signal<DemoTourStep[]>(DemoTourService.STEPS);
  readonly stepIndex = signal(0);
  readonly currentStep = computed(() => this.steps()[this.stepIndex()] ?? null);

  private demoAccountService = inject(DemoAccountService);
  private pmDemoLanding = inject(PmDemoLandingService);
  private pmDemoTour = inject(PmDemoTourService);
  private navCtrl = inject(CustomNavController);
  private router = inject(Router);
  private leaveTimer: any = null;

  constructor() {
    // The page stays interactive under the tour dock, so follow the router: jump
    // the dock to whichever tour page the user navigates to themselves, and bow
    // out gracefully when they leave the tour path (they're exploring on their
    // own — the guide's job is done).
    this.router.events.pipe(takeUntilDestroyed()).subscribe((event) => {
      if (!(event instanceof NavigationEnd) || !this.active() || this.leaving()) {
        return;
      }
      const segment = this.segmentFromUrl(event.urlAfterRedirects || event.url);
      const current = this.currentStep();
      if (!current || segment === current.route) {
        return;
      }
      if (!this.jumpToRoute(segment)) {
        this.skip('navigated_away');
      }
    });
  }

  /**
   * Begin the tour on the page the visitor landed on. Called when the demo welcome
   * card is dismissed — the only entry that guarantees the user is settled on their
   * landing variant page with nothing else covering the screen.
   */
  async startAfterWelcome(): Promise<void> {
    // Resolve the experiment rather than reading its sticky cache: this is the one moment
    // the tour can start, so a flag response that hasn't landed yet would silently drop a
    // `guided-tour` visitor into the control experience. Non-desktop and unassigned
    // visitors resolve to null and fall out here.
    await this.pmDemoTour.resolveVariant();
    if (this.active() || !this.canShow()) {
      return;
    }
    const order = this.rotatedFromRoute(this.currentRouteSegment());
    this.steps.set(order);
    this.stepIndex.set(0);
    this.persist();
    this.show();
    this.pmDemoLanding.capture('demo_tour_started', { landing_page: order[0].id });
  }

  /**
   * Re-show the tour after a mid-tour reload. Only resumes when the reload landed
   * on a tour page (usually the stop they were on); the dock picks up from there.
   */
  resumeIfPending(): void {
    if (this.active() || !this.canShow()) {
      return;
    }
    // The welcome card (or the entrance sweep) still owns the screen; its dismissal
    // will start the tour instead.
    if (this.demoAccountService.entrancePhase() !== 'idle') {
      return;
    }
    const saved = this.readState();
    if (!saved) {
      return;
    }
    const steps = saved.order.map((id) => DemoTourService.STEPS.find((s) => s.id === id)!);
    const routeIndex = steps.findIndex((s) => s.route === this.currentRouteSegment());
    if (routeIndex < 0) {
      // Reloaded somewhere off the tour path — leave the saved state for a later
      // visit rather than showing a dock about a page they're not on.
      return;
    }
    this.steps.set(steps);
    this.stepIndex.set(routeIndex);
    this.persist();
    this.show();
  }

  next(): void {
    const index = this.stepIndex();
    if (index >= this.steps().length - 1) {
      this.complete();
      return;
    }
    this.stepIndex.set(index + 1);
    this.persist();
    const step = this.currentStep();
    if (step && this.currentRouteSegment() !== step.route) {
      this.navCtrl.navigateRoot(step.route);
    }
    this.pmDemoLanding.capture('demo_tour_page_viewed', {
      page: step?.id,
      step: index + 2,
    });
  }

  skip(reason = 'dismissed'): void {
    this.pmDemoLanding.capture('demo_tour_skipped', {
      page: this.currentStep()?.id,
      step: this.stepIndex() + 1,
      reason,
    });
    this.close();
  }

  private complete(): void {
    this.pmDemoLanding.capture('demo_tour_completed');
    this.close();
  }

  // Jump the tour to the stop for the given route segment (the user navigated
  // there themselves). Returns false when the segment isn't part of the tour.
  private jumpToRoute(segment: string): boolean {
    const index = this.steps().findIndex((s) => s.route === segment);
    if (index < 0) {
      return false;
    }
    if (index !== this.stepIndex()) {
      this.stepIndex.set(index);
      this.persist();
      this.pmDemoLanding.capture('demo_tour_page_viewed', {
        page: this.steps()[index].id,
        step: index + 1,
        via: 'navigation',
      });
    }
    return true;
  }

  private canShow(): boolean {
    try {
      return this.demoAccountService.isDemoSession()
        && this.pmDemoTour.isTourVariant()
        && localStorage.getItem(DemoTourService.DONE_KEY) !== 'true';
    } catch {
      return false;
    }
  }

  private show(): void {
    clearTimeout(this.leaveTimer);
    this.leaving.set(false);
    this.active.set(true);
  }

  private close(): void {
    try {
      localStorage.setItem(DemoTourService.DONE_KEY, 'true');
      localStorage.removeItem(DemoTourService.STATE_KEY);
    } catch { /* best-effort */ }
    this.leaving.set(true);
    clearTimeout(this.leaveTimer);
    this.leaveTimer = setTimeout(() => {
      this.active.set(false);
      this.leaving.set(false);
    }, 380);
  }

  // Rotate the fixed cycle so the visitor's current page comes first. Unknown
  // routes (shouldn't happen — the tour starts on a landing variant) keep the
  // default order.
  private rotatedFromRoute(segment: string): DemoTourStep[] {
    const steps = DemoTourService.STEPS;
    const start = steps.findIndex((s) => s.route === segment);
    if (start <= 0) {
      return [...steps];
    }
    return [...steps.slice(start), ...steps.slice(0, start)];
  }

  private currentRouteSegment(): string {
    return this.segmentFromUrl(this.router.url || '');
  }

  private segmentFromUrl(url: string): string {
    const path = url.split('?')[0].split('#')[0];
    return path.split('/').filter(Boolean)[0] || '';
  }

  private persist(): void {
    try {
      const state: PersistedTourState = {
        order: this.steps().map((s) => s.id),
        index: this.stepIndex(),
      };
      localStorage.setItem(DemoTourService.STATE_KEY, JSON.stringify(state));
    } catch { /* best-effort */ }
  }

  private readState(): PersistedTourState | null {
    try {
      const raw = localStorage.getItem(DemoTourService.STATE_KEY);
      if (!raw) {
        return null;
      }
      const state: PersistedTourState = JSON.parse(raw);
      const ids = DemoTourService.STEPS.map((s) => s.id);
      const valid = Array.isArray(state?.order)
        && state.order.length === ids.length
        && state.order.every((id) => ids.includes(id))
        && Number.isInteger(state?.index)
        && state.index >= 0
        && state.index < ids.length;
      return valid ? state : null;
    } catch {
      return null;
    }
  }
}
